import { Card, CardContent, CardHeader, CardTitle } from "@/src/components/shadcn/ui/card";
import { cn } from "@/src/lib/shadcn-utils";

const HEADER_VARIANT_CLASS = {
  outline: "bg-muted/40",
  success: "border-success/40 bg-success/50",
  destructive: "border-destructive/40 bg-destructive/50",
} as const;

export type ValerieCardHeaderVariant = keyof typeof HEADER_VARIANT_CLASS;

/**
 * Standard Valerie card: consistent header (muted icon + base-size title on the left, optional
 * actions slot on the right) divided from the content by a bottom border, so cards across the
 * app line up visually.
 *
 * Keep it dumb: callers own everything inside `children` and `actions`. Prefer this over raw
 * Card/CardHeader composition when a card has a titled header.
 */
export function ValerieCard({
  title,
  icon,
  actions,
  className,
  variant = "outline",
  contentClassName,
  children,
}: {
  title: React.ReactNode;
  icon?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
  variant?: ValerieCardHeaderVariant;
  contentClassName?: string;
  children: React.ReactNode;
}) {
  return (
    <Card className={cn("overflow-hidden", className)}>
      <CardHeader
        className={cn(
          "flex-row items-center justify-between gap-2 space-y-0 border-b py-3",
          HEADER_VARIANT_CLASS[variant]
        )}
      >
        <CardTitle className="flex min-w-0 items-center gap-2 text-base font-semibold">
          {icon ? (
            <span
              className={cn(
                "shrink-0 [&>svg]:h-4 [&>svg]:w-4",
                variant === "outline" ? "text-muted-foreground" : "text-current opacity-90"
              )}
            >
              {icon}
            </span>
          ) : null}
          <span className="min-w-0 truncate">{title}</span>
        </CardTitle>
        {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
      </CardHeader>
      <CardContent className={cn("pt-4", contentClassName)}>{children}</CardContent>
    </Card>
  );
}
